/**
 * DiagramCentricView - Diagram-first maturity assessment view
 *
 * The diagram is the central focus. Compact level tabs at the top,
 * large interactive diagram in the center, and condensed progression
 * guidance below.
 *
 * Key design principles:
 * - Diagram is the hero, not cards
 * - Level selector is compact horizontal tabs
 * - Progression guidance is concise (2-4 bullet points)
 * - Clicking a level updates the diagram immediately
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Check,
  Target,
  ChevronRight,
  ArrowRight,
  AlertTriangle,
  CheckCircle,
  Info,
  Settings
} from 'lucide-react';
import JourneyDiagramCanvas from './JourneyDiagramCanvas';
import { getDiagramTemplate } from '../data/maturityDiagramTemplates';
import './DiagramCentricView.css';

const MATURITY_LEVELS = [
  { key: 'TRADITIONAL', label: 'Traditional', shortLabel: 'T', color: '#6b7280' },
  { key: 'INITIAL', label: 'Initial', shortLabel: 'I', color: '#eab308' },
  { key: 'ADVANCED', label: 'Advanced', shortLabel: 'A', color: '#3b82f6' },
  { key: 'OPTIMAL', label: 'Optimal', shortLabel: 'O', color: '#10b981' }
];

/**
 * Get the progression requirements from current level to next level
 */
function getProgressionRequirements(functionCode, currentLevel) {
  const currentIdx = MATURITY_LEVELS.findIndex(l => l.key === currentLevel);
  if (currentIdx >= MATURITY_LEVELS.length - 1) return null; // Already at Optimal

  const nextLevel = MATURITY_LEVELS[currentIdx + 1];
  const nextTemplate = getDiagramTemplate(functionCode, nextLevel.key);

  if (!nextTemplate) return null;

  // The keyCapabilities of the next level are what you need to progress
  return {
    nextLevel: nextLevel,
    requirements: nextTemplate.keyCapabilities,
    highlights: nextTemplate.nodes.filter(n => n.isNew || n.isHighlight).map(n => n.label)
  };
}

export default function DiagramCentricView({
  functionCode,
  functionName,
  pillarCode,
  currentLevel = 'TRADITIONAL',
  targetLevel = null,
  onSetCurrentLevel,
  onSetTargetLevel,
  onStartAssessment
}) {
  // Selected level for diagram display (defaults to current)
  const [selectedLevel, setSelectedLevel] = useState(currentLevel);
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [highlightNew, setHighlightNew] = useState(true);

  // Get template for selected level
  const template = useMemo(() => {
    return getDiagramTemplate(functionCode, selectedLevel);
  }, [functionCode, selectedLevel]);

  // Get progression requirements
  const progression = useMemo(() => {
    return getProgressionRequirements(functionCode, currentLevel);
  }, [functionCode, currentLevel]);

  // Handle level tab click
  const handleLevelClick = useCallback((levelKey) => {
    setSelectedLevel(levelKey);
  }, []);

  // Handle setting current level
  const handleSetAsCurrent = useCallback(() => {
    onSetCurrentLevel?.(selectedLevel);
  }, [selectedLevel, onSetCurrentLevel]);

  // Handle setting target level
  const handleSetAsTarget = useCallback(() => {
    onSetTargetLevel?.(selectedLevel);
  }, [selectedLevel, onSetTargetLevel]);

  if (!template) {
    return (
      <div className="dcv-empty">
        <Info size={48} />
        <p>No diagram available for {functionName} at {selectedLevel}</p>
      </div>
    );
  }

  const currentLevelIdx = MATURITY_LEVELS.findIndex(l => l.key === currentLevel);
  const selectedLevelIdx = MATURITY_LEVELS.findIndex(l => l.key === selectedLevel);
  const selectedLevelInfo = MATURITY_LEVELS[selectedLevelIdx];

  return (
    <div className="dcv-container">
      {/* Compact Level Selector */}
      <div className="dcv-level-selector">
        <div className="dcv-level-tabs">
          {MATURITY_LEVELS.map((level, idx) => {
            const isCurrent = level.key === currentLevel;
            const isTarget = level.key === targetLevel;
            const isSelected = level.key === selectedLevel;
            const isPast = idx < currentLevelIdx;

            return (
              <React.Fragment key={level.key}>
                <button
                  className={`dcv-level-tab ${isSelected ? 'dcv-level-tab--selected' : ''} ${isPast ? 'dcv-level-tab--past' : ''}`}
                  style={{ '--level-color': level.color }}
                  onClick={() => handleLevelClick(level.key)}
                >
                  <span className="dcv-level-dot" />
                  <span className="dcv-level-name">{level.label}</span>
                  {isCurrent && (
                    <span className="dcv-level-badge dcv-level-badge--current">
                      <Check size={10} /> Current
                    </span>
                  )}
                  {isTarget && !isCurrent && (
                    <span className="dcv-level-badge dcv-level-badge--target">
                      <Target size={10} /> Target
                    </span>
                  )}
                </button>
                {idx < MATURITY_LEVELS.length - 1 && (
                  <ChevronRight className="dcv-level-chevron" size={16} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Toggle controls */}
        <div className="dcv-controls">
          <button
            className={`dcv-control-btn ${showAnnotations ? 'dcv-control-btn--active' : ''}`}
            onClick={() => setShowAnnotations(!showAnnotations)}
            title="Show/hide annotations"
          >
            <Info size={14} />
            Notes
          </button>
          <button
            className={`dcv-control-btn ${highlightNew ? 'dcv-control-btn--active' : ''}`}
            onClick={() => setHighlightNew(!highlightNew)}
            title="Highlight new components"
          >
            <CheckCircle size={14} />
            New
          </button>
        </div>
      </div>

      {/* Diagram Header */}
      <div className="dcv-diagram-header">
        <div className="dcv-diagram-title">
          <span
            className="dcv-diagram-level-badge"
            style={{ backgroundColor: selectedLevelInfo.color }}
          >
            {selectedLevelInfo.label}
          </span>
          <h3>{template.name}</h3>
        </div>
        <p className="dcv-diagram-desc">{template.description}</p>

        {/* Quick Actions */}
        <div className="dcv-quick-actions">
          {selectedLevel !== currentLevel && (
            <button className="dcv-action-btn" onClick={handleSetAsCurrent}>
              <Check size={14} />
              Set as Current
            </button>
          )}
          {selectedLevel !== currentLevel && selectedLevel !== targetLevel && selectedLevelIdx > currentLevelIdx && (
            <button className="dcv-action-btn dcv-action-btn--target" onClick={handleSetAsTarget}>
              <Target size={14} />
              Set as Target
            </button>
          )}
        </div>
      </div>

      {/* Central Diagram */}
      <div className="dcv-diagram-container">
        <JourneyDiagramCanvas
          template={{
            ...template,
            annotations: showAnnotations ? template.annotations : []
          }}
          showAnnotations={showAnnotations}
          highlightNew={highlightNew}
        />

        {/* Legend */}
        <div className="dcv-diagram-legend">
          <div className="dcv-legend-item">
            <span className="dcv-legend-swatch dcv-legend-swatch--existing" />
            <span>Existing</span>
          </div>
          {highlightNew && (
            <div className="dcv-legend-item">
              <span className="dcv-legend-swatch dcv-legend-swatch--new" />
              <span>New at this level</span>
            </div>
          )}
        </div>
      </div>

      {/* Condensed Progression Panel */}
      <div className="dcv-progression-panel">
        {selectedLevel === currentLevel && progression ? (
          <>
            <div className="dcv-progression-header">
              <ArrowRight size={18} />
              <span>To Progress to</span>
              <span
                className="dcv-progression-target"
                style={{ color: progression.nextLevel.color }}
              >
                {progression.nextLevel.label}
              </span>
            </div>
            <ul className="dcv-progression-list">
              {progression.requirements.slice(0, 4).map((req, idx) => (
                <li key={idx}>
                  <Settings size={14} />
                  <span>{req}</span>
                </li>
              ))}
            </ul>
            {onStartAssessment && (
              <button className="dcv-assess-btn" onClick={onStartAssessment}>
                <CheckCircle size={16} />
                Where am I today?
              </button>
            )}
          </>
        ) : selectedLevel === 'OPTIMAL' ? (
          <div className="dcv-progression-complete">
            <CheckCircle size={24} style={{ color: '#10b981' }} />
            <span>Optimal is the highest maturity level</span>
          </div>
        ) : (
          <>
            <div className="dcv-viewing-header">
              <Info size={18} />
              <span>Viewing {selectedLevelInfo.label} Level Architecture</span>
            </div>
            <div className="dcv-key-capabilities">
              <h4>Key Capabilities at {selectedLevelInfo.label}:</h4>
              <ul>
                {template.keyCapabilities.slice(0, 4).map((cap, idx) => (
                  <li key={idx}>
                    <CheckCircle size={14} style={{ color: selectedLevelInfo.color }} />
                    <span>{cap}</span>
                  </li>
                ))}
              </ul>
            </div>
            {template.risks && template.risks.length > 0 && (
              <div className="dcv-risks">
                <h4>Considerations:</h4>
                <ul>
                  {template.risks.slice(0, 2).map((risk, idx) => (
                    <li key={idx}>
                      <AlertTriangle size={14} />
                      <span>{risk}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
